'use client'

import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import Image from 'next/image'
import { isLoggedInSelector } from 'auth/selectors'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { useSiteData } from '@repo/web-ui/hooks/use-site-data'
import { ActivateTrial } from 'payments/activate-trial-button'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import { UPGRADE_SOURCES } from 'payments/upgrade-sources'
import { BillingPeriod } from '@repo/cf-user/gen/types'

import { LFS_URL } from '@web/utils/env'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'

export function UpgradeToYearly({ className }: { className?: string }) {
  const t = useTranslations('prod.product_description_page.buy')
  const { isPending: isLoadingSiteData } = useSiteData()
  const { isPending: isLoadingUser, data: userData } = useUser()
  const isEligibleForAllAccessYearlyUpgrade =
    userData?.subscriptionAccess.allAccess.isEligibleForYearlyUpgrade
  const isMonthlyUser =
    userData?.subscriptionAccess?.allAccess?.subscription?.billingPeriod === BillingPeriod.Month
  const isLoggedIn = isLoggedInSelector({ user: userData })
  const { prices, currency } = usePricesForUser()
  const price = `${currency.symbol}${prices.yearlyPromoMonthlySplit}`

  if (
    isLoadingUser ||
    isLoadingSiteData ||
    isMonthlyUser ||
    (!isEligibleForAllAccessYearlyUpgrade && isLoggedIn)
  ) {
    return null
  }

  return (
    <div
      className={tw(
        'rounded-4 text-cf-dreamy-cloud-1000 animate-fade-in relative w-full overflow-hidden bg-[#fff4e1] p-4',
        className,
      )}
    >
      <div
        className="mb-1 flex items-center justify-between"
        onClick={() => {
          savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.PDP_TOGGLE)
        }}
        role="button"
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.PDP_TOGGLE)
          }
        }}
      >
        <div className="text-16 font-bold">{t('switch_yearly.title')}</div>
        <ActivateTrial.Toggle upgradeSource={UPGRADE_SOURCES.MARKETPLACE_AI_PRODUCT_PAGE_TOGGLE} />
      </div>
      <div className="text-14 z-10" data-testid="upgrade-to-yearly-details">
        {t.rich('switch_yearly.details', {
          b: str => <b className="font-bold">{str}</b>,
          price,
        })}
      </div>
      <Image
        src={`${LFS_URL}/web/pages/content/pdp/upgrade/toggle-bg-xs.jpeg`}
        alt=""
        width={1860}
        height={289}
        className="absolute left-0 top-0 -z-10 h-full w-full object-none object-left"
        unoptimized
      />
    </div>
  )
}
